import * as React from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { Icon } from '../../icon';
import { Badge } from '../../badge';
import { Typography } from '../../typography';
import { MenuType, MenuState } from '../theme';
import { IBaseComponentProps } from '../../types';
import { IMenuItemRenderProps } from './types';
import { StackPanel } from '../../stack-panel';
import { extractNumberAsString } from '../../../utils';
import { selectThemeSemantics } from '../../../styling';

const Arrow = styled(Icon).attrs({
    className: String('ap-menu-itemArrow'),
    iconName: 'ap-right-arrow-thin.outline'
})`
    transition: all 0.15s ease-in-out;
    z-index: 1;
`;

interface IMenuItemContentProps extends IMenuItemRenderProps, IBaseComponentProps<HTMLDivElement> { }

const MenuItemContentBase = React.memo((props: IMenuItemContentProps) => {
    const { className, elementRef, itemInfo, hasChildren, onSelect } = props;
    const { iconName, iconSize, label, additionalInfo } = itemInfo;
    const { semantics: { gap: { xl } } } = useTheme();
    const gap = React.useMemo(() => extractNumberAsString(xl), [xl]);

    const handleKeyPress = React.useCallback((ev: React.KeyboardEvent) => {
        if (ev.key === 'Enter') {
            onSelect();
        }
    }, [onSelect]);
    // Flyout menu keyboard navigation doesn't support nested childs

    return (
        <StackPanel className={className} onClick={onSelect} tabIndex={0} onKeyDown={handleKeyPress} ref={elementRef} justifyContent="space-between" alignment="center" gap={gap}>
            <StackPanel alignment="center" gap={gap}>
                <Icon className="ap-menu-icon" iconName={iconName!} iconSize={iconSize} hidden={!iconName} />
                <Typography.Small hidden={!label}>{label}</Typography.Small>
            </StackPanel>
            <StackPanel justifyContent="right" alignment="center" gap={gap}>
                <Badge label={additionalInfo!} hidden={!additionalInfo} />
                <Arrow iconSize="12" hidden={!hasChildren} />
            </StackPanel>
        </StackPanel>
    );
});

MenuItemContentBase.displayName = 'MenuItemContentBase';

const getThemeColorGetter = (type: MenuType, state: MenuState) => (theme: DefaultTheme) => theme.menu[type].colors.item[state];

const getMenuThemeStyles = memoizeFunction((type: MenuType, state: MenuState) => {
    const getThemeProperties = getThemeColorGetter(type, state);
    return css`
        border-bottom: 1px solid ${({ theme }) => getThemeProperties(theme).borderColor};
        background-color: ${({ theme }) => getThemeProperties(theme).backgroundColor};
        color: ${({ theme }) => getThemeProperties(theme).text};

        ${Icon} {
            fill: ${({ theme }) => getThemeProperties(theme).mainIcon};
        }

        ${Arrow} {
            fill: ${({ theme }) => getThemeProperties(theme).menuIcon};
        }
    `;
});

export const MenuItemContent = styled(MenuItemContentBase).attrs({
    className: String('ap-menu-itemContent')
})`
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding: ${selectThemeSemantics(({ gap }) => `${gap.l} ${gap.xxl}`)};
    cursor: ${({ cursorType = 'default' }) => cursorType};
    outline-color: ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    ${({ isItemSelected, hasChildren, highlightSelectionPath, isItemHovered, menuType }) => {

        if (isItemHovered) {
            return getMenuThemeStyles(menuType, 'hovered');
        }

        if (!isItemSelected) {
            return getMenuThemeStyles(menuType, 'default');
        }

        if (hasChildren) {
            return getMenuThemeStyles(menuType, highlightSelectionPath ? 'expanded' : 'default');
        }

        return getMenuThemeStyles(menuType, 'selected');
    }};

    :hover {
        ${({ isItemSelected, hasChildren, highlightSelectionPath, menuType }) => {
        if (hasChildren && isItemSelected) {
            return getMenuThemeStyles(menuType, highlightSelectionPath ? 'expanded' : 'default');
        }
        if (!hasChildren && isItemSelected) {
            return getMenuThemeStyles(menuType, 'selected');
        }
        return getMenuThemeStyles(menuType, 'hovered');
    }}
    };
`;

MenuItemContent.displayName = 'MenuItemContent';
